import { DANE_VALIDATION_TEST_RESULT_FORM, DEFAULT_TEST_RESULT_FORM, IRcaTestResult } from "../models/IRcaTestResult";
import { JsonService } from "./JsonService";
import { HttpError, IHttpClient, FetchHttpClient } from "./HttpClient";

/**
 * The server data contract for the get test result response
 */
export interface ITestStatus {
  /**
   * The execution status
   */
  status: "NotFound" | "NotStarted" | "Running" | "Complete" | "Faulted";

  /**
   * The execution id (GUID)
   */
  executionId: string;

  /**
   * The time the test was submitted
   */
  timeSubmitted?: Date;

  /**
   * The status description (interim execution status)
   */
  statusDescription?: string;
}

/**
 * The result download format.
 */
export enum ResultFormat {
  Html = "html",
  Xml = "xml",
}

/**
 * The test result repository service interface
 */
export interface ITestResultRepository {
  /**
   * Gets the status of a test
   *
   * @param language - The language to use to retrieve the result
   * @param resultId - The result id to retrieve
   * @returns - The test status
   */
  getTestStatusAsync(language: string, resultId: string): Promise<ITestStatus>;

  /**
   * Gets the result of a completed test
   *
   * @param language - The language of the result to retrieve
   * @param resultId - The result id to retrieve
   * @returns The test result
   */
  getTestResultAsync(language: string, resultId: string): Promise<IRcaTestResult | null>;

  /**
   * Gets the download URL for a test result.
   *
   * @param language - The language to use for the download link
   * @param resultId - The result ID
   * @param displayFormType - The display form type
   * @param format - The result format (html or xml)
   */
  getDownloadUrl(language: string, resultId: string, displayFormType: string, format: ResultFormat): string;
}

/**
 * The test result repository service
 */
export class TestResultRepository extends JsonService implements ITestResultRepository {
  /**
   * Initializes a new instance of the TestResultRepository class.
   *
   * @param apiVersion - The API version
   * @param httpClient - The optional HTTP client
   */
  public constructor(apiVersion: string, httpClient: IHttpClient | null = null) {
    super(apiVersion, httpClient ?? new FetchHttpClient());
  }

  /**
   * Gets the status of a test
   *
   * @param language - The language to use to retrieve the result
   * @param resultId - The result id to retrieve
   * @returns - The test status
   */
  public getTestStatusAsync(language: string, resultId: string): Promise<ITestStatus> {
    return this.getJsonAsync(`/api/${this.apiVersion}/result/${resultId}/status?lang=${language}`, false);
  }

  /**
   * Gets the result of a completed test
   *
   * @param language - The language of the result to retrieve
   * @param resultId - The result id to retrieve
   * @returns The test result
   */
  public async getTestResultAsync(language: string, resultId: string): Promise<IRcaTestResult | null> {
    try {
      return await this.getJsonAsync(`/api/${this.apiVersion}/result/${resultId}?lang=${language}`, false);
    } catch (error) {
      if (error instanceof HttpError && error.statusCode === 404) {
        // For now a null result here will mean "not found"
        return null;
      }

      throw error;
    }
  }

  /**
   * Gets the download URL for a test result.
   *
   * @param language - The language to use for the download link
   * @param resultId - The result id
   * @param displayFormType - The display form type
   * @param format - The result format (html or xml)
   * @returns the link to download the test result
   */
  public getDownloadUrl(language: string, resultId: string, displayFormType: string, format: ResultFormat): string {
    switch (displayFormType) {
      default:
      case DEFAULT_TEST_RESULT_FORM:
        return `/api/${this.apiVersion}/result/${resultId}?lang=${language}&format=${format}`;

      case DANE_VALIDATION_TEST_RESULT_FORM:
        return `/DownloadDaneValidationTestResult?TestId=${resultId}&Format=${format}`;
    }
  }
}
